import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Clock, Calendar, BookOpen } from 'lucide-react';
import GoogleAd from '../components/GoogleAd';
import { loadBlogPosts } from '../utils/blogLoader';
import { BlogPost } from '../types';


// Featured article card component
const FeaturedArticleCard: React.FC<{ post: BlogPost }> = ({ post }) => {
  return (
    <Link to={`/blog/${post.slug}`} className="block group">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg overflow-hidden transition-all duration-300 hover:shadow-xl h-full border border-gray-100 dark:border-gray-700">
        <div className="relative h-96">
          <img src={post.imageUrl} alt={post.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-8">
            {post.categories && post.categories.length > 0 && (
              <span className="inline-block px-3 py-1 rounded-full bg-blue-600 text-white text-xs font-semibold mb-3">
                {post.categories[0]}
              </span>
            )}
            <h2 className="text-3xl font-bold text-white mb-3 group-hover:text-blue-200 transition-colors">
              {post.title}
            </h2>
            <p className="text-white/80 mb-4">
              {post.excerpt}
            </p>
            <div className="flex items-center text-white/70 text-sm">
              <Calendar className="h-4 w-4 mr-1" />
              <span>{new Date(post.publishDate).toLocaleDateString('nb-NO')}</span>
              <span className="mx-2">•</span>
              <Clock className="h-4 w-4 mr-1" />
              <span>{post.readTime} min</span>
            </div>
          </div>
        </div>
      </div>
    </Link>
  );
};

// Article card component
const ArticleCard: React.FC<{ post: BlogPost }> = ({ post }) => {
  return (
    <Link to={`/blog/${post.slug}`} className="block group">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm overflow-hidden transition-all duration-300 hover:shadow-md h-full border border-gray-100 dark:border-gray-700">
        <div className="h-48 overflow-hidden">
          <img 
            src={post.imageUrl} 
            alt={post.title} 
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
        </div>
        <div className="p-5">
          {post.categories && post.categories.length > 0 && (
            <span className="inline-block px-2 py-1 rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 text-xs font-semibold mb-2">
              {post.categories[0]}
            </span>
          )}
          <h3 className="font-bold text-lg mb-2 text-gray-900 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
            {post.title}
          </h3>
          <p className="text-gray-600 dark:text-gray-300 text-sm mb-3">
            {post.excerpt}
          </p>
          <div className="flex items-center text-gray-500 dark:text-gray-400 text-xs mt-3 pt-3 border-t border-gray-100 dark:border-gray-700">
            <Calendar className="h-3 w-3 mr-1" />
            <span>{new Date(post.publishDate).toLocaleDateString('nb-NO')}</span>
            <span className="mx-2">•</span>
            <Clock className="h-3 w-3 mr-1" />
            <span>{post.readTime} min</span>
          </div>
        </div>
      </div>
    </Link>
  );
};

// Main Blog page component
const Blog: React.FC = () => {
  const [blogPosts, setBlogPosts] = React.useState<BlogPost[]>([]);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    const loadPosts = async () => {
      try {
        const posts = await loadBlogPosts();
        setBlogPosts(posts);
      } catch (error) {
        console.error('Failed to load blog posts:', error);
      } finally {
        setLoading(false);
      }
    };

    loadPosts();
  }, []);

  if (loading) {
    return (
      <main className="flex-grow container mx-auto px-4 py-8">
        <div className="max-w-6xl mx-auto">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 dark:border-blue-400 mx-auto mb-4"></div>
            <p className="text-gray-600 dark:text-gray-300">Laster blogginnlegg...</p>
          </div>
        </div>
      </main>
    );
  }

  // Get featured post and remaining posts
  const featuredPost = blogPosts[0];
  const regularPosts = blogPosts.slice(1);

  // For structured data - convert categories and topics to schema format
  const blogTopics = Array.from(new Set(blogPosts.flatMap(post => post.tags || post.categories)));

  return (
    <>
      <Helmet>
        <title>Strømbloggen | Artikler om strømpriser og energisparing | Strømkalkulator.no</title>
        <meta name="description" content="Les våre artikler om strømpriser, energisparing, strømstøtte og smart strømforbruk. Få tips og råd for å redusere strømregningen din." />
        <meta name="keywords" content="strømblogg, strømpriser, energisparing, strømstøtte, strømforbruk, elbillading, strømkalkulator" />
        <link rel="canonical" href="https://stromkalkulator.no/blog" />
        
        {/* Open Graph meta tags */}
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Strømbloggen | Artikler om strømpriser og energisparing" />
        <meta property="og:description" content="Les våre artikler om strømpriser, energisparing, strømstøtte og smart strømforbruk. Få tips og råd for å redusere strømregningen din." />
        <meta property="og:image" content={featuredPost?.imageUrl} />
        <meta property="og:url" content="https://stromkalkulator.no/blog" />
        <meta property="og:site_name" content="Strømkalkulator.no" />
        
        {/* Twitter Card data */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Strømbloggen | Artikler om strømpriser og energisparing" />
        <meta name="twitter:description" content="Les våre artikler om strømpriser, energisparing, strømstøtte og smart strømforbruk. Få tips og råd for å redusere strømregningen din." />
        <meta name="twitter:image" content={featuredPost?.imageUrl} />
        
        {/* Schema.org structured data for Blog */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Blog",
            "headline": "Strømbloggen | Artikler om strømpriser og energisparing",
            "description": "Les våre artikler om strømpriser, energisparing, strømstøtte og smart strømforbruk. Få tips og råd for å redusere strømregningen din.",
            "image": featuredPost?.imageUrl,
            "publisher": {
              "@type": "Organization",
              "name": "Strømkalkulator.no",
              "logo": {
                "@type": "ImageObject",
                "url": "https://stromkalkulator.no/logo.png"
              }
            },
            "url": "https://stromkalkulator.no/blog",
            "author": {
              "@type": "Person",
              "name": "Torstein"
            },
            "keywords": blogTopics.join(", "),
            "blogPost": blogPosts.map(post => ({
              "@type": "BlogPosting",
              "headline": post.title,
              "description": post.excerpt,
              "image": post.imageUrl,
              "datePublished": post.publishDate,
              "author": {
                "@type": "Person",
                "name": post.author
              },
              "url": `https://stromkalkulator.no/blog/${post.slug}`
            }))
          })}
        </script>
        
        {/* BreadcrumbList structured data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              {
                "@type": "ListItem",
                "position": 1,
                "name": "Hjem",
                "item": "https://stromkalkulator.no"
              },
              {
                "@type": "ListItem",
                "position": 2,
                "name": "Blog",
                "item": "https://stromkalkulator.no/blog"
              }
            ]
          })}
        </script>
      </Helmet>

      <main className="flex-grow container mx-auto px-4 py-8">
        <div className="max-w-6xl mx-auto">
          {/* Blog Header */}
          <div className="text-center mb-12">
            <div className="flex justify-center mb-3">
              <div className="bg-blue-100 dark:bg-blue-800 p-3 rounded-full">
                <BookOpen className="h-8 w-8 text-blue-600 dark:text-blue-400" />
              </div>
            </div>
            <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-3">
              Strømbloggen
            </h1>
            <p className="text-lg text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
              Oppdaterte artikler om strømpriser, energisparing og strømstøtte
            </p>
          </div>

          {/* Featured post */}
          <div className="mb-16">
            <h2 className="text-2xl font-bold text-gray-800 dark:text-white mb-6">
              Siste nytt
            </h2>
            {featuredPost && <FeaturedArticleCard post={featuredPost} />}
          </div>

          {/* Regular posts grid */}
          <h2 className="text-2xl font-bold text-gray-800 dark:text-white mb-6">Siste artikler</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
            {regularPosts.map((post) => (
              <ArticleCard key={post.id} post={post} />
            ))}
          </div>

          {/* Google Ad */}
          <div className="mb-12 bg-white dark:bg-gray-800 p-4 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
            <GoogleAd
              slot="3068465121"
              format="fluid"
              layout="in-article"
              style={{
                display: 'block',
                margin: '0 auto',
                textAlign: 'center',
                minHeight: '100px'
              }}
            />
          </div>
          
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-xl shadow-sm p-10 border border-blue-100 dark:border-blue-800">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Les vår strømblogg!</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-sm dark:border dark:border-gray-700">
                <div className="bg-blue-100 dark:bg-blue-800 w-12 h-12 flex items-center justify-center rounded-full mb-4">
                  <svg className="w-6 h-6 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                  </svg>
                </div>
                <h3 className="text-lg font-semibold mb-2 text-gray-900 dark:text-white">Høy kvalitet</h3>
                <p className="text-gray-600 dark:text-gray-300">Vi er veldig interessert i strøm, og har over lengre tid opparbeidet oss god kunnskap på området. All data er hentet fra troverdige kilder </p>
              </div>
              <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-sm dark:border dark:border-gray-700">
                <div className="bg-blue-100 dark:bg-blue-800 w-12 h-12 flex items-center justify-center rounded-full mb-4">
                  <svg className="w-6 h-6 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                  </svg>
                </div>
                <h3 className="text-lg font-semibold mb-2 text-gray-900 dark:text-white">Alltid oppdatert</h3>
                <p className="text-gray-600 dark:text-gray-300">Følg med på de siste endringene i strømstøtteordningen og prisutvikling i markedet.</p>
              </div>
              <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-sm dark:border dark:border-gray-700">
                <div className="bg-blue-100 dark:bg-blue-800 w-12 h-12 flex items-center justify-center rounded-full mb-4">
                  <svg className="w-6 h-6 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"></path>
                  </svg>
                </div>
                <h3 className="text-lg font-semibold mb-2 text-gray-900 dark:text-white">Praktiske tips</h3>
                <p className="text-gray-600 dark:text-gray-300">Konkrete råd for å redusere strømforbruket og spare penger på strømregningen.</p>
              </div>
            </div>
          </div>
        </div>
      </main>
    </>
  );
};

export default Blog;